import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { Box, Flex, Text, Button as ChakraButton, useMediaQuery, useTheme } from "@chakra-ui/react";
import { Button } from "./buttons";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { colors, lightColorWithDarkTheme, lightThemeColorValues, ON_JUNGLE_DARK_PAGE } from "../constants/GlobalStyles";
import {
  useStreakStore,
  pageStore,
  useReviewDeckStore,
  useGlobalWebsiteStore,
  useSubscriptionUpsellStore,
  userStore,
} from "../zustand/globalStore";
import { CustomerSupportPanelExpanded, useOpenCustomerSupportFeedbackOptions } from "./customer-support/useOpenCustomerSupportFeedbackOptions";
import IconSupportBadge from "../icons/IconSupportBadge";
import { AppTooltip } from "./AppTooltip";
import { trackEvent, getUserSubscriptionPlanAndLimits, getCurrentStreakLength } from "../backend/utils/helper-methods";
import ProfileButtonWithDropdown from "./ProfileButtonWithDropdown";
import JungleLogo from "../images/JungleLogo";
import {
  IconArrowBarToRight,
  IconSparkle,
  IconHouse,
  IconCards,
  IconFire,
  IconTrophy,
  IconDots,
  IconMicrophone,
  IconBackpack,
} from "../icons";
import { useHelperMethodsHook } from "../contextsAndHooks/HelperMethodsHook";
import {
  MIN_WIDTH_OF_TABLET_PORTAIT_VIEW,
  MAX_WIDTH_OF_TABLET_PORTAIT_VIEW,
  UserType,
} from "../constants/GlobalTypesAndEnumsAndConstants";
import { isDesktop, isTablet } from "../backend/utils/detect-device";
import IconLightningOutline from "../icons/IconLightningOutline";
import { UserSubscriptionPlan, ReasonForShowingSubscriptionUpsell } from "../constants/GlobalTypesAndEnumsAndConstants";
import { useTranslate } from "@tolgee/react";
import { shouldShowLiveRecordingNavigation } from "./liveRecordingVisibility";
import monkeyPortrait from "../voice/assets/monkey-contact.svg";
import { useTutorUiEnabled } from "../features/tutor/integration/tutorAvailability";
import { tutorRouteTarget } from "../features/tutor/integration/tutorLocation";
import { TutorSidebarEntry, type TutorSidebarEntryConfiguration } from "../features/tutor/navigation/TutorSidebarEntry";

type NavigationBarProps = { tutorEntry?: TutorSidebarEntryConfiguration };
const defaultTutorEntry: TutorSidebarEntryConfiguration = {
  tutorPortraitUrl: monkeyPortrait,
  bodyTarget: tutorRouteTarget({ view: "home" }),
  chatTarget: tutorRouteTarget({ view: "home" }),
  callAction: { state: "unavailable" },
};
const allClassesTutorTarget = tutorRouteTarget({ view: "conversation", scope: { kind: "general" } });

// The phone's bottom bar draws the tab of the page that is open in the brand green, the product's color for
// "chosen": its icon, and the line above it. (The line was a pale grey, which read as no mark at all.)
// The other tabs carry the same line without a color, so an icon does not move when its tab is opened.
const CURRENT_TAB_COLOR = colors.primaryAction.default;
const CURRENT_TAB_LINE = `3px solid ${CURRENT_TAB_COLOR}`;
const NOT_CURRENT_TAB_LINE = "3px solid transparent";

// A tab of the phone's bar, from the top: the current tab's line, the icon (in an area as tall as the "create"
// tab's circle, so every icon sits at one height), the word under it.
const PHONE_TAB_HEIGHT = "60px";
const PHONE_TAB_ICON_AREA_HEIGHT = "36px";
const PHONE_TAB_GAP_BETWEEN_ICON_AND_LABEL = "2px";
const PHONE_TAB_PADDING_BELOW_LABEL = "3px";

// How much of the bottom of the window the phone's bar covers, as a CSS variable on <html> while the bar is
// on screen (and not set otherwise). A sheet fixed to the bottom of the window reads it to sit on top of the
// bar instead of over it: `bottom: var(--jungle-phone-navigation-bar-height, 0px)`.
export const PHONE_NAVIGATION_BAR_HEIGHT_CSS_VARIABLE = "--jungle-phone-navigation-bar-height";

/** The my classes link's label, for copy that sends a learner to that page: it names exactly what the bar shows.
 * Kept in this file so the label stays one translation with one context. */
export function useMyClassesNavigationLabel(): string {
  const { t } = useTranslate();
  return t("my_classes", "my classes");
}

const NavigationBar: React.FC<NavigationBarProps> = ({ tutorEntry = defaultTutorEntry }) => {
  const tutorUiEnabled = useTutorUiEnabled();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { t } = useTranslate();
  const isRightToLeft = useTheme().direction === "rtl";

  const { sendEventsToMobileApp } = useHelperMethodsHook();

  // zustand states
  const { isNavigationBarCollapsed, setIsNavigationBarCollapsedByLearner, setIsNavigationBarCollapsedForViewport } = useGlobalWebsiteStore((state) => state);
  const user = userStore((state) => state.currentUser);
  const { currentStreakLength, setCurrentStreakLength } = useStreakStore((state) => state);
  const { isRunningAsMobileApp } = pageStore((state) => state);
  const { currentReviewDeck } = useReviewDeckStore((state) => state);
  const { showSubscriptionUpsellModal } = useSubscriptionUpsellStore((state) => state);

  // Opens the floating support panel on its feedback options. The hook subscribes only to the panel's
  // actions (not `isPanelOpen`), so toggling the panel never re-renders the entire navigation bar.
  const handleOpenCustomerSupportChatbotFromNavigationBar = useOpenCustomerSupportFeedbackOptions();

  // local states
  const [isProfileDropDownMenuOpen, setIsProfileDropDownMenuOpen] = useState<boolean>(false);
  const [isCollapseButtonHovered, setIsCollapseButtonHovered] = useState(false);
  const [isOnTabletPortraitView, setIsTabletPortraitView] = useState(
    typeof window !== "undefined" && window.innerWidth > 767 && window.innerWidth <= 1024
  );

  // refs
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const desktopMenuRef = useRef<HTMLDivElement>(null);
  const phoneNavigationBarRef = useRef<HTMLDivElement>(null);
  const phoneMoreButtonRef = useRef<HTMLButtonElement>(null);

  // variables
  const [isMobile] = useMediaQuery(`(max-width: ${MIN_WIDTH_OF_TABLET_PORTAIT_VIEW}px)`);
  const isCurrentlyReviewingADeck =
    window.location.pathname.includes("/review-deck/") || window.location.pathname.includes("/learning-journey/");
  // Get user subscription information
  const { userNumOfMonthlyGenerationsLeft, userSubscriptionPlan, managerSubscriptionAccount } = useMemo(
    () => getUserSubscriptionPlanAndLimits(user),
    [user]
  );
  const haveCardsBeenGeneratedBefore = localStorage.getItem("haveCardsBeenGeneratedBefore");
  const hasUserGeneratedASetOfQuestions = useMemo(() => {
    return haveCardsBeenGeneratedBefore || (user ? user?.number_of_sets_of_cards_generated > 0 : false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [user?.number_of_sets_of_cards_generated, haveCardsBeenGeneratedBefore]);
  const isOnHomepage = location.pathname === "/";
  const isOnMyClassesPage = location.pathname === "/my-classes";
  const isOnLeaderboardPage = location.pathname === "/leaderboard";
  const isOnTutorPage = location.pathname === "/tutor" || location.pathname.startsWith("/tutor/");
  const isFourthPhoneTabCurrent = tutorUiEnabled ? isOnTutorPage : isOnLeaderboardPage;
  // The card's call button is not inside any class: it opens the Tutor's "all classes" chat and starts a call
  // there, which searches every class's selected sources (owner, 2026-10-29). A class's own call button stays in it.
  const sidebarTutorEntry = useMemo<TutorSidebarEntryConfiguration>(() => tutorEntry !== defaultTutorEntry ? tutorEntry : {
    ...tutorEntry,
    callAction: { state: "ready", onActivate: () => navigate(allClassesTutorTarget.pathname, { state: { tutorStartCall: Date.now() } }) },
  }, [tutorEntry, navigate]);
  const isOnBackpackPage = location.pathname === "/backpack";
  const isOnSettingsPage = location.pathname === "/settings";
  const isUserAStudentOrTeacher = useMemo(() => {
    return user?.user_type === UserType.Student || user?.user_type === UserType.TeacherOrProfessor;
  }, [user?.user_type]);

  // Show live recording on desktop browsers, including touch-enabled desktops.
  const shouldShowLiveRecording = shouldShowLiveRecordingNavigation({
    isDesktopDevice: isDesktop,
    isMobileDevice: isMobile,
    isTabletDevice: isTablet,
  });

  useEffect(() => {
    if (!currentStreakLength) {
      // first time the user loads the page, we need to get the current streak length
      if (user && !currentStreakLength) {
        const getStreakData = async () => {
          const streakLength = await getCurrentStreakLength(user.id);
          setCurrentStreakLength(streakLength);
        };
        getStreakData();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [user?.id]);

  useEffect(() => {
    const handleResize = () => {
      setIsTabletPortraitView(
        window.innerWidth > MIN_WIDTH_OF_TABLET_PORTAIT_VIEW && window.innerWidth <= MAX_WIDTH_OF_TABLET_PORTAIT_VIEW
      );
      setIsNavigationBarCollapsedForViewport(
        window.innerWidth > MIN_WIDTH_OF_TABLET_PORTAIT_VIEW && window.innerWidth <= MAX_WIDTH_OF_TABLET_PORTAIT_VIEW
      );
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, []);

  const streakLengthWithFireIcon = () => {
    return (
      <Box
        as={RouterLink}
        to="/streak-and-stats"
        marginInlineEnd={isNavigationBarCollapsed ? "0px" : "40px"}
        marginInlineStart={isNavigationBarCollapsed ? "0px" : "20px"}
        display="flex"
        flexDirection="row"
        alignItems="center"
        cursor="pointer"
        _hover={{ textDecoration: "none" }}
        onClick={() => trackEvent("Streak Calendar Opened", { context: "Side Bar" }, user)}
      >
        <IconFire width={30} height={30} />
        <Text fontSize="16px" fontWeight={500} lineHeight="24px" marginInlineStart="8px" color={lightColorWithDarkTheme("#854037", colors.accentColors.okayDark)}>
          {currentStreakLength !== null && currentStreakLength !== undefined
            ? `${currentStreakLength} ${t("day_streak", "day streak")}`
            : `- ${t("day_streak", "day streak")}`}
        </Text>
      </Box>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Use the appropriate ref based on the current view
      const currentRef = isMobile ? mobileMenuRef : desktopMenuRef;
      if (currentRef?.current && !currentRef?.current?.contains(event.target as Node) && isProfileDropDownMenuOpen) {
        setIsProfileDropDownMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileDropDownMenuOpen, isMobile]);

  // A layout effect, so a sheet that reads the variable is already in its place on the first paint.
  useLayoutEffect(() => {
    const phoneNavigationBarElement = phoneNavigationBarRef.current;
    if (!isMobile || !phoneNavigationBarElement) return undefined;
    document.documentElement.style.setProperty(
      PHONE_NAVIGATION_BAR_HEIGHT_CSS_VARIABLE,
      `${phoneNavigationBarElement.getBoundingClientRect().height}px`
    );
    return () => {
      document.documentElement.style.removeProperty(PHONE_NAVIGATION_BAR_HEIGHT_CSS_VARIABLE);
    };
  }, [isMobile]);

  // Render Mobile Navigation Bar
  if (isMobile) {
    // The word under a tab's icon: one line of 11px, so that five of them fit side by side on a 390px phone.
    const renderPhoneTabLabel = (label: string, isCurrentTab: boolean) => (
      <Text
        fontSize="11px"
        lineHeight="14px"
        fontWeight={500}
        color={isCurrentTab ? CURRENT_TAB_COLOR : colors.secondaryAction.default}
        maxWidth="100%"
        noOfLines={1}
      >
        {label}
      </Text>
    );

    return (
      <Flex
        ref={phoneNavigationBarRef}
        data-customer-support-mobile-navigation="true"
        bg={colors.surface.white}
        borderTop={`1.5px solid ${lightColorWithDarkTheme(lightThemeColorValues.surface.secondary, colors.surface.border)}`}
        // The bar is the color of a card, and cards scroll under it. On the light theme everything is white
        // and nobody expects a step; on a dark theme what lies above is lighter or casts a shadow, so the bar
        // gets the edge every card has and a shadow that falls upward.
        sx={{ [ON_JUNGLE_DARK_PAGE]: { boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.35)" } }}
        width="100%"
        direction="row"
        justify="stretch"
        align="center"
        // No padding above: a tab reaches the bar's top edge, where the current tab's line is drawn
        padding="0px 16px 28px"
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        zIndex={1000}
      >
        <Flex
          as={RouterLink}
          to="/"
          direction="column"
          align="center"
          justify="flex-end"
          gap={PHONE_TAB_GAP_BETWEEN_ICON_AND_LABEL}
          height={PHONE_TAB_HEIGHT}
          paddingBottom={PHONE_TAB_PADDING_BELOW_LABEL}
          flex={1}
          minWidth={0}
          cursor="pointer"
          borderTop={isOnHomepage ? CURRENT_TAB_LINE : NOT_CURRENT_TAB_LINE}
          outline="none"
          _focus={{ outline: "none" }}
          _active={{ bg: "transparent" }}
          sx={{
            WebkitTapHighlightColor: "transparent",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none",
          }}
        >
          <Flex height={PHONE_TAB_ICON_AREA_HEIGHT} align="center" justify="center" flexShrink={0}>
            <IconHouse
              width={isMobile ? 22 : 16}
              height={isMobile ? 22 : 16}
              color={isOnHomepage ? CURRENT_TAB_COLOR : colors.secondaryAction.default}
              strokeWidth={isOnHomepage ? 2 : 1.5}
            />
          </Flex>
          {renderPhoneTabLabel(t("phone_navigation_home", "home"), isOnHomepage)}
        </Flex>

        <Flex
          as={RouterLink}
          to="/my-classes"
          direction="column"
          align="center"
          justify="flex-end"
          gap={PHONE_TAB_GAP_BETWEEN_ICON_AND_LABEL}
          height={PHONE_TAB_HEIGHT}
          paddingBottom={PHONE_TAB_PADDING_BELOW_LABEL}
          flex={1}
          minWidth={0}
          cursor="pointer"
          borderTop={isOnMyClassesPage ? CURRENT_TAB_LINE : NOT_CURRENT_TAB_LINE}
          outline="none"
          _focus={{ outline: "none" }}
          _active={{ bg: "transparent" }}
          sx={{
            WebkitTapHighlightColor: "transparent",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none",
          }}
        >
          <Flex height={PHONE_TAB_ICON_AREA_HEIGHT} align="center" justify="center" flexShrink={0}>
            <IconCards
              width={isMobile ? 24 : 20}
              height={isMobile ? 24 : 20}
              color={isOnMyClassesPage ? CURRENT_TAB_COLOR : colors.secondaryAction.default}
              strokeWidth={isOnMyClassesPage ? 1.75 : 1.5}
            />
          </Flex>
          {renderPhoneTabLabel(
            isUserAStudentOrTeacher
              ? t("phone_navigation_classes", "classes")
              : t("phone_navigation_folders", "folders"),
            isOnMyClassesPage
          )}
        </Flex>

        <Flex
          as={RouterLink}
          to="/upload-document"
          direction="column"
          justify="flex-end"
          align="center"
          gap={PHONE_TAB_GAP_BETWEEN_ICON_AND_LABEL}
          flex={1}
          minWidth={0}
          height={PHONE_TAB_HEIGHT}
          paddingBottom={PHONE_TAB_PADDING_BELOW_LABEL}
          cursor="pointer"
          outline="none"
          _focus={{ outline: "none" }}
          _active={{ bg: "transparent" }}
          sx={{
            WebkitTapHighlightColor: "transparent",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none",
          }}
        >
          <Box
            width={PHONE_TAB_ICON_AREA_HEIGHT}
            height={PHONE_TAB_ICON_AREA_HEIGHT}
            flexShrink={0}
            padding="0px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap="8px"
            borderRadius="100px"
            background={colors.primaryAction.navigationButtonFill}
            outline="none"
            _focus={{ outline: "none" }}
            _active={{ bg: colors.primaryAction.navigationButtonFill }}
            sx={{
              WebkitTapHighlightColor: "transparent",
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              userSelect: "none",
            }}
          >
            <IconSparkle
              width={isMobile ? 20 : 16}
              height={isMobile ? 20 : 16}
              color={colors.primaryAction.hover}
              strokeWidth={1.5}
            />
          </Box>
          {renderPhoneTabLabel(t("phone_navigation_create", "create"), false)}
        </Flex>

        <Flex
          as={RouterLink}
          to={tutorUiEnabled ? tutorEntry.bodyTarget : "/leaderboard"}
          state={tutorUiEnabled ? tutorEntry.bodyTarget.state : undefined}
          aria-current={tutorUiEnabled && isOnTutorPage ? "page" : undefined}
          direction="column"
          align="center"
          justify="flex-end"
          gap={PHONE_TAB_GAP_BETWEEN_ICON_AND_LABEL}
          height={PHONE_TAB_HEIGHT}
          paddingBottom={PHONE_TAB_PADDING_BELOW_LABEL}
          flex={1}
          minWidth={0}
          cursor="pointer"
          borderTop={isFourthPhoneTabCurrent ? CURRENT_TAB_LINE : NOT_CURRENT_TAB_LINE}
          outline="none"
          _focus={{ outline: "none" }}
          _active={{ bg: "transparent" }}
          sx={{
            WebkitTapHighlightColor: "transparent",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none",
          }}
        >
          <Flex height={PHONE_TAB_ICON_AREA_HEIGHT} align="center" justify="center" flexShrink={0}>
            {tutorUiEnabled ? <Box as="img" src={tutorEntry.tutorPortraitUrl} alt="" width={isMobile ? "28px" : "20px"} height={isMobile ? "28px" : "20px"} borderRadius="50%"
              boxShadow={isFourthPhoneTabCurrent ? `0 0 0 2px ${CURRENT_TAB_COLOR}` : "none"} /> : <IconTrophy
              width={isMobile ? 22 : 16}
              height={isMobile ? 22 : 16}
              color={isOnLeaderboardPage ? CURRENT_TAB_COLOR : colors.secondaryAction.default}
              strokeWidth={isOnLeaderboardPage ? 1.75 : 1.5}
            />}
          </Flex>
          {renderPhoneTabLabel(tutorUiEnabled ? t("tutor.title", "tutor") : t("phone_navigation_league", "league"), isFourthPhoneTabCurrent)}
        </Flex>

        <Flex
          direction="column"
          align="center"
          justify="flex-end"
          gap={PHONE_TAB_GAP_BETWEEN_ICON_AND_LABEL}
          height={PHONE_TAB_HEIGHT}
          paddingBottom={PHONE_TAB_PADDING_BELOW_LABEL}
          flex={1}
          minWidth={0}
          position="relative"
          ref={mobileMenuRef}
          onClick={() => {
            setIsProfileDropDownMenuOpen(!isProfileDropDownMenuOpen);
          }}
          cursor="pointer"
          borderTop={isOnSettingsPage ? CURRENT_TAB_LINE : NOT_CURRENT_TAB_LINE}
          outline="none"
          _focus={{ outline: "none" }}
          _active={{ bg: "transparent" }}
          sx={{
            WebkitTapHighlightColor: "transparent",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none",
          }}
        >
          {/* The tab's control for a keyboard and a screen reader: a real button, named by its label ("more").
              It has no handler of its own: its press bubbles to the tab, which also takes a press beside the
              icon. The menu it opens is drawn outside it, so no control sits inside another. */}
          <Flex
            as="button"
            type="button"
            ref={phoneMoreButtonRef}
            aria-haspopup="menu"
            aria-expanded={isProfileDropDownMenuOpen}
            direction="column"
            align="center"
            gap={PHONE_TAB_GAP_BETWEEN_ICON_AND_LABEL}
            maxWidth="100%"
            // The control spans its tab (at least 44px), not just the word "more" (it measured 26px wide).
            width="100%"
            minWidth="44px"
          >
            <Flex height={PHONE_TAB_ICON_AREA_HEIGHT} align="center" justify="center" flexShrink={0}>
              <IconDots
                width={isMobile ? 24 : 18}
                height={isMobile ? 24 : 18}
                color={isOnSettingsPage ? CURRENT_TAB_COLOR : colors.secondaryAction.default}
                strokeWidth={isOnSettingsPage ? 1.75 : 1.5}
              />
            </Flex>
            {renderPhoneTabLabel(t("phone_navigation_more", "more"), isOnSettingsPage)}
          </Flex>

          {isProfileDropDownMenuOpen && (
            // The menu opens upward from here. This height puts its bottom edge 8px above the bar, clear of
            // the current tab's line and of the "create" circle (it used to hang a few pixels over the bar).
            <Box
              position="absolute"
              bottom="55px"
              insetInlineEnd="0px"
              // Escape closes the menu and hands focus back to "more", as a menu does. This bar, not the
              // menu, decides when the menu closes, so the menu's own Escape does nothing here; and the menu
              // keeps the key from travelling further up, so it is caught on the way down.
              onKeyDownCapture={(event: React.KeyboardEvent) => {
                if (event.key !== "Escape") return;
                setIsProfileDropDownMenuOpen(false);
                phoneMoreButtonRef.current?.focus();
              }}
            >
              <ProfileButtonWithDropdown
                isProfileDropDownMenuOpen={isProfileDropDownMenuOpen}
                setIsProfileDropDownMenuOpen={setIsProfileDropDownMenuOpen}
                user={user}
                isMobile={true}
                mobileTriggerProvidedByParent={true}
                currentReviewDeck={currentReviewDeck}
                userSubscriptionPlan={userSubscriptionPlan as string}
                userNumOfMonthlyGenerationsLeft={userNumOfMonthlyGenerationsLeft}
                managerSubscriptionAccount={managerSubscriptionAccount as string}
                isRunningAsMobileApp={isRunningAsMobileApp}
                sendEventsToMobileApp={sendEventsToMobileApp}
                hasUserGeneratedASetOfQuestions={hasUserGeneratedASetOfQuestions}
                isCurrentlyReviewingADeck={isCurrentlyReviewingADeck}
              />
            </Box>
          )}
        </Flex>
      </Flex>
    );
  }

  // Desktop Sidebar
  return (
    <Flex
      direction="column"
      justifyContent="space-between"
      alignSelf="stretch"
      padding={isNavigationBarCollapsed ? "40px 28px 28px" : "40px 28px 28px"}
      width={isNavigationBarCollapsed ? "132px" : "100%"}
      maxWidth="292px"
      bg={colors.surface.offWhite}
      borderInlineEnd={`2px solid ${colors.surface.secondary}`}
      transition="width 0.3s ease, padding 0.3s ease"
      height="100vh"
      overflowY={tutorUiEnabled ? "auto" : undefined}
      gap={tutorUiEnabled ? "24px" : undefined}
    >
      {/* Top Section */}
      <Flex direction="column" alignSelf="stretch" gap="24px">
        {/* Logo and Toggle */}
        <Flex
          justifyContent={isNavigationBarCollapsed ? "center" : "space-between"}
          alignItems="center"
          alignSelf="stretch"
          // The icon rail keeps the Jungle logo at its top (desktop-boards-31), the expand arrow under it.
          direction={isNavigationBarCollapsed ? "column" : "row"}
          gap={isNavigationBarCollapsed ? "14px" : undefined}
          paddingY={isNavigationBarCollapsed ? undefined : "10px"}
          paddingInlineStart={isNavigationBarCollapsed ? undefined : "24px"}
          paddingInlineEnd={isNavigationBarCollapsed ? undefined : "13px"}
        >
          <Box
            as={RouterLink}
            to="/"
            // The logo is a picture: the link says where it goes
            aria-label={t("homepage", "homepage")}
            height={isNavigationBarCollapsed ? "28px" : "24px"}
            width={isNavigationBarCollapsed ? "52px" : "87px"}
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            _hover={{ textDecoration: "none" }}
          >
            <JungleLogo height={isNavigationBarCollapsed ? 30 : 46} />
          </Box>
          <Box
            // A real button with a name: it was a clickable box that a keyboard could not reach. On a tablet
            // held upright the bar is always collapsed and the arrow is not drawn, so the empty button is
            // kept away from the keyboard and from screen readers.
            as="button"
            type="button"
            aria-label={
              isNavigationBarCollapsed
                ? t("navigation_expand_sidebar", "expand sidebar")
                : t("navigation_collapse_sidebar", "collapse sidebar")
            }
            aria-expanded={!isNavigationBarCollapsed}
            aria-hidden={isOnTabletPortraitView ? true : undefined}
            tabIndex={isOnTabletPortraitView ? -1 : undefined}
            cursor="pointer"
            onClick={() => setIsNavigationBarCollapsedByLearner(!isNavigationBarCollapsed)}
            transform={`${isNavigationBarCollapsed ? "rotate(180deg)" : "rotate(0deg)"}${isRightToLeft ? " scaleX(-1)" : ""}`}
            transition="transform 0.3s ease"
            display="flex"
            alignItems="center"
            onMouseEnter={() => setIsCollapseButtonHovered(true)}
            onMouseLeave={() => setIsCollapseButtonHovered(false)}
          >
            {!isOnTabletPortraitView && (
              <IconArrowBarToRight
                width={16}
                height={16}
                color={isCollapseButtonHovered ? colors.secondaryAction.hover : colors.secondaryAction.default}
              />
            )}
          </Box>
        </Flex>

        {/* Navigation Buttons */}
        <Flex direction="column" gap="20px" alignItems={isNavigationBarCollapsed ? "center" : "stretch"}>
          {/* Generate New Questions Button */}
          <Button
            as={RouterLink}
            to="/upload-document"
            title={!isNavigationBarCollapsed ? t("generate_new_questions", "generate new questions") : ""}
            shouldAllowTitleToWrap
            // The collapsed bar shows icons only: each link keeps its words as its name
            aria-label={t("generate_new_questions", "generate new questions")}
            buttonPrimaryOrSecondaryType="primary"
            buttonStyleType="light"
            // A light button is filled with `primaryAction.light2`. Here that color is given the navigation
            // button's own value (the same mint on the light theme; greener on a dark sidebar, where the
            // tint was the selected page's pill again).
            style={{ "--jungle-primary-action-light-2": colors.primaryAction.navigationButtonFill } as React.CSSProperties}
            size="small"
            gap="8px"
            isRoundButton={isNavigationBarCollapsed}
            justifyContent={isNavigationBarCollapsed ? "center" : "flex-start"}
            LeftIcon={(props) => <IconSparkle {...props} color={colors.primaryAction.hover} height={16} width={16} />}
          />
          {/* Homepage Button */}
          <Button
            as={RouterLink}
            to="/"
            title={!isNavigationBarCollapsed ? `${t("homepage", "homepage")}` : ""}
            aria-label={t("homepage", "homepage")}
            justifyContent={isNavigationBarCollapsed ? "center" : "flex-start"}
            textColor={isOnHomepage ? colors.secondaryAction.pressed : colors.secondaryAction.default}
            padding={isNavigationBarCollapsed ? "12px 0px" : "12px 24px"}
            buttonPrimaryOrSecondaryType="secondary"
            buttonStyleType="ghost"
            size="small"
            gap="8px"
            iconSpacing={isNavigationBarCollapsed ? "0px" : "16px"}
            isRoundButton={isNavigationBarCollapsed}
            pressed={isOnHomepage}
            pressedBackgroundColor={lightColorWithDarkTheme("#F8FAF9", colors.surface.secondary)}
            LeftIcon={(props) => (
              <IconHouse
                {...props}
                width={18}
                height={18}
                color={isOnHomepage ? colors.secondaryAction.pressed : colors.secondaryAction.default}
              />
            )}
          />
          {/* My Classes Button */}
          <Button
            as={RouterLink}
            to="/my-classes"
            title={
              !isNavigationBarCollapsed
                ? isUserAStudentOrTeacher
                  ? t("my_classes", "my classes")
                  : t("my_folders", "my folders")
                : ""
            }
            aria-label={isUserAStudentOrTeacher ? t("my_classes", "my classes") : t("my_folders", "my folders")}
            justifyContent={isNavigationBarCollapsed ? "center" : "flex-start"}
            textColor={isOnMyClassesPage ? colors.secondaryAction.pressed : colors.secondaryAction.default}
            padding={isNavigationBarCollapsed ? "12px 0" : "12px 24px"}
            buttonPrimaryOrSecondaryType="secondary"
            buttonStyleType="ghost"
            size="small"
            gap="8px"
            isRoundButton={isNavigationBarCollapsed}
            pressed={isOnMyClassesPage}
            pressedBackgroundColor={lightColorWithDarkTheme("#F8FAF9", colors.surface.secondary)}
            LeftIcon={(props) => (
              <IconCards
                {...props}
                width={22}
                height={22}
                color={isOnMyClassesPage ? colors.secondaryAction.pressed : colors.secondaryAction.default}
              />
            )}
          />
          {/* Live Recording Button (desktop only) */}
          {shouldShowLiveRecording && (
            <Button
              as={RouterLink}
              to="/recording"
              title={
                !isNavigationBarCollapsed
                  ? isUserAStudentOrTeacher
                    ? t("record_lecture", "record lecture")
                    : t("live_recording", "live recording")
                  : ""
              }
              aria-label={
                isUserAStudentOrTeacher ? t("record_lecture", "record lecture") : t("live_recording", "live recording")
              }
              justifyContent={isNavigationBarCollapsed ? "center" : "flex-start"}
              textColor={
                pathname.includes("/recording") ? colors.secondaryAction.pressed : colors.secondaryAction.default
              }
              padding={isNavigationBarCollapsed ? "12px 0" : "12px 24px"}
              buttonPrimaryOrSecondaryType="secondary"
              buttonStyleType="ghost"
              size="small"
              gap="8px"
              isRoundButton={isNavigationBarCollapsed}
              pressed={pathname.includes("/recording")}
              pressedBackgroundColor={lightColorWithDarkTheme("#F8FAF9", colors.surface.secondary)}
              LeftIcon={() => (
                <IconMicrophone
                  width={22}
                  height={22}
                  color={
                    pathname.includes("/recording") ? colors.secondaryAction.pressed : colors.secondaryAction.default
                  }
                />
              )}
              onClick={(e: React.MouseEvent) => {
                // Let the browser handle modifier-key clicks natively (cmd/ctrl/shift/middle-click)
                // so the link opens in a new tab/window using the `to` href above.
                const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1;
                if (isModifiedClick) return;

                // Regular click: prevent RouterLink's default navigation and run gating logic.
                e.preventDefault();

                if (!user) {
                  navigate("/login");
                  return;
                }

                const { userSubscriptionPlan, userNumOfMonthlyLiveRecordingsLeft } =
                  getUserSubscriptionPlanAndLimits(user);

                const isFreeUserOutOfRecordings =
                  userSubscriptionPlan === UserSubscriptionPlan.FREE && userNumOfMonthlyLiveRecordingsLeft <= 0;

                if (isFreeUserOutOfRecordings) {
                  const { showSubscriptionUpsellModal } = useSubscriptionUpsellStore.getState();
                  showSubscriptionUpsellModal({
                    reasonForUpsell: ReasonForShowingSubscriptionUpsell.MonthlyRecordingLimitReached,
                    contextForShowingSubscriptionUpsell: "Live Recording Button Nav Bar",
                    subscriptionPlanToShow: UserSubscriptionPlan.SUPER_LEARNER,
                  });
                  return;
                }

                navigate("/recording");
              }}
            />
          )}
          {/* Leaderboard Button */}
          <Button
            as={RouterLink}
            to="/leaderboard"
            title={!isNavigationBarCollapsed ? t("leaderboard", "leaderboard") : ""}
            aria-label={t("leaderboard", "leaderboard")}
            justifyContent={isNavigationBarCollapsed ? "center" : "flex-start"}
            textColor={isOnLeaderboardPage ? colors.secondaryAction.pressed : colors.secondaryAction.default}
            padding={isNavigationBarCollapsed ? "12px 0" : "12px 24px"}
            buttonPrimaryOrSecondaryType="secondary"
            buttonStyleType="ghost"
            size="small"
            gap="8px"
            iconSpacing={isNavigationBarCollapsed ? "0px" : "18px"}
            isRoundButton={isNavigationBarCollapsed}
            pressed={isOnLeaderboardPage}
            pressedBackgroundColor={lightColorWithDarkTheme("#F8FAF9", colors.surface.secondary)}
            LeftIcon={(props) => (
              <IconTrophy
                {...props}
                width={17}
                height={17}
                color={isOnLeaderboardPage ? colors.secondaryAction.pressed : colors.secondaryAction.default}
              />
            )}
          />
          {/* Backpack Button */}
          <Button
            as={RouterLink}
            to="/backpack"
            title={!isNavigationBarCollapsed ? t("backpack", "backpack") : ""}
            aria-label={t("backpack", "backpack")}
            justifyContent={isNavigationBarCollapsed ? "center" : "flex-start"}
            textColor={isOnBackpackPage ? colors.secondaryAction.pressed : colors.secondaryAction.default}
            padding={isNavigationBarCollapsed ? "12px 0" : "12px 24px"}
            buttonPrimaryOrSecondaryType="secondary"
            buttonStyleType="ghost"
            size="small"
            gap="8px"
            iconSpacing={isNavigationBarCollapsed ? "0px" : "18px"}
            isRoundButton={isNavigationBarCollapsed}
            pressed={isOnBackpackPage}
            pressedBackgroundColor={lightColorWithDarkTheme("#F8FAF9", colors.surface.secondary)}
            LeftIcon={(props) => (
              <IconBackpack
                {...props}
                width={17}
                height={17}
                color={isOnBackpackPage ? colors.secondaryAction.pressed : colors.secondaryAction.default}
              />
            )}
          />
          {/* Upgrade Button — only plans below Pro see it. Pro users upgrade to
              Elite via the profile dropdown item instead; Elite has nothing to buy. */}
          {userSubscriptionPlan !== UserSubscriptionPlan.SUPER_LEARNER &&
            userSubscriptionPlan !== UserSubscriptionPlan.ELITE && (
              <Button
                title={!isNavigationBarCollapsed ? t("upgrade", "upgrade") : ""}
                aria-label={t("upgrade", "upgrade")}
                justifyContent={isNavigationBarCollapsed ? "center" : "flex-start"}
                textColor={colors.secondaryAction.default}
                padding={isNavigationBarCollapsed ? "12px 0" : "12px 12px 12px 24px"}
                buttonPrimaryOrSecondaryType="primary"
                buttonStyleType="ghost"
                size="small"
                gap="8px"
                iconSpacing={isNavigationBarCollapsed ? "0px" : "18px"}
                isRoundButton={isNavigationBarCollapsed}
                LeftIcon={(props) => (
                  <IconLightningOutline
                    {...props}
                    width={16}
                    height={16}
                    color={colors.primaryAction.default}
                    style={{
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = colors.primaryAction.hover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = colors.primaryAction.default;
                    }}
                  />
                )}
                onClick={() => {
                  showSubscriptionUpsellModal({
                    subscriptionPlanToShow: UserSubscriptionPlan.SUPER_LEARNER,
                    contextForShowingSubscriptionUpsell: "Navigation Bar Upgrade Button",
                  });
                }}
              />
            )}
          {tutorUiEnabled && <TutorSidebarEntry {...sidebarTutorEntry} isCollapsed={isNavigationBarCollapsed} isCurrent={isOnTutorPage} />}
        </Flex>
      </Flex>

      {/* Bottom Profile Section */}
      {!isNavigationBarCollapsed && (
        <Flex direction="column" gap="20px">
          {user && (
            <Box display="flex" justifyContent={isNavigationBarCollapsed ? "center" : "flex-start"}>
              {streakLengthWithFireIcon()}
            </Box>
          )}
          <Box ref={desktopMenuRef}>
            <ProfileButtonWithDropdown
              isProfileDropDownMenuOpen={isProfileDropDownMenuOpen}
              setIsProfileDropDownMenuOpen={setIsProfileDropDownMenuOpen}
              user={user}
              currentReviewDeck={currentReviewDeck}
              userSubscriptionPlan={userSubscriptionPlan as string}
              userNumOfMonthlyGenerationsLeft={userNumOfMonthlyGenerationsLeft}
              managerSubscriptionAccount={managerSubscriptionAccount as string}
              isRunningAsMobileApp={isRunningAsMobileApp}
              sendEventsToMobileApp={sendEventsToMobileApp}
              hasUserGeneratedASetOfQuestions={hasUserGeneratedASetOfQuestions}
              isCurrentlyReviewingADeck={isCurrentlyReviewingADeck}
            />
          </Box>

          {/* Feedback Button with expanding lines */}
          <Flex align="center" width="100%">
            <Box className="feedback-line" height="0.5px" bg={colors.secondaryAction.inactive} width="30%" />
            <CustomerSupportPanelExpanded>
              {(expanded) => (
                <ChakraButton
                  onClick={handleOpenCustomerSupportChatbotFromNavigationBar}
                  aria-expanded={expanded}
                  variant="text"
                  size="sm"
                  mx="10px"
                  fontSize="14px"
                  fontWeight="500"
                  color={colors.secondaryAction.default}
                  _hover={{ color: colors.secondaryAction.hover, textDecoration: "none" }}
                >
                  {t("give_feedback", "get help / give feedback")}
                </ChakraButton>
              )}
            </CustomerSupportPanelExpanded>
            <Box className="feedback-line" height="0.5px" bg={colors.secondaryAction.inactive} width="30%" />
          </Flex>
        </Flex>
      )}

      {isNavigationBarCollapsed && (
        <Flex direction="column" gap="14px">
          {/* The collapsed bar has no room for "get help / give feedback": the profile menu's support icon, above
              the avatar, opens the same support panel. */}
          <AppTooltip label={t("give_feedback", "get help / give feedback")} placement="right" openDelay={200}>
            <Flex justify="center">
              <CustomerSupportPanelExpanded>
                {(expanded) => (
                  <Button
                    title=""
                    aria-label={t("give_feedback", "get help / give feedback")}
                    aria-expanded={expanded}
                    justifyContent="center"
                    textColor={colors.secondaryAction.default}
                    padding="12px 0"
                    buttonPrimaryOrSecondaryType="secondary"
                    buttonStyleType="ghost"
                    size="small"
                    gap="8px"
                    iconSpacing="0px"
                    isRoundButton
                    LeftIcon={(props) => (
                      <IconSupportBadge {...props} width={18} height={18} color={colors.secondaryAction.default} />
                    )}
                    onClick={handleOpenCustomerSupportChatbotFromNavigationBar}
                  />
                )}
              </CustomerSupportPanelExpanded>
            </Flex>
          </AppTooltip>
          <Box ref={desktopMenuRef}>
            <ProfileButtonWithDropdown
              isProfileDropDownMenuOpen={isProfileDropDownMenuOpen}
              setIsProfileDropDownMenuOpen={setIsProfileDropDownMenuOpen}
              user={user}
              currentReviewDeck={currentReviewDeck}
              userSubscriptionPlan={userSubscriptionPlan as string}
              userNumOfMonthlyGenerationsLeft={userNumOfMonthlyGenerationsLeft}
              managerSubscriptionAccount={managerSubscriptionAccount as string}
              isRunningAsMobileApp={isRunningAsMobileApp}
              sendEventsToMobileApp={sendEventsToMobileApp}
              hasUserGeneratedASetOfQuestions={hasUserGeneratedASetOfQuestions}
              isCurrentlyReviewingADeck={isCurrentlyReviewingADeck}
            />
          </Box>
        </Flex>
      )}
    </Flex>
  );
};

export default NavigationBar;
